import type { Amenity } from '@wineryxplorer/shared';

interface AmenitiesDisplayProps {
  value: Amenity[];
  className?: string;
  maxDisplay?: number;
  showAll?: boolean;
}

const AMENITY_LABELS = {
  'scenic-views': 'Scenic Views',
  'historic-building': 'Historic Building',
  'family-friendly': 'Family-Friendly',
  'pet-friendly': 'Pet-Friendly',
  'outdoor-seating': 'Outdoor Seating',
  'live-music': 'Live Music',
  'food-service': 'Food Service',
  'tours-available': 'Tours Available',
  'event-space': 'Event Space',
  'wheelchair-accessible': 'Wheelchair Accessible',
} as const;

const AMENITY_ICONS = {
  'scenic-views': '🏞️',
  'historic-building': '🏛️',
  'family-friendly': '👨‍👩‍👧‍👦',
  'pet-friendly': '🐕',
  'outdoor-seating': '🌳',
  'live-music': '🎵',
  'food-service': '🍽️',
  'tours-available': '🚶‍♂️',
  'event-space': '🎉',
  'wheelchair-accessible': '♿',
} as const;

export const AmenitiesDisplay = ({ 
  value = [], 
  className = '', 
  maxDisplay = 5,
  showAll = false 
}: AmenitiesDisplayProps) => {
  if (value.length === 0) {
    return null;
  }

  const displayAmenities = showAll ? value : value.slice(0, maxDisplay);
  const remainingCount = value.length - maxDisplay;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {displayAmenities.map((amenity) => {
        const label = AMENITY_LABELS[amenity];
        const icon = AMENITY_ICONS[amenity];
        
        return (
          <span
            key={amenity}
            className="inline-flex items-center px-3 py-2 rounded-full text-xs sm:text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200 touch-manipulation"
            title={label}
            aria-label={`Amenity: ${label}`}
            style={{ minHeight: '32px' }}
          >
            <span className="mr-1 text-sm" aria-hidden="true">{icon}</span>
            <span className="truncate max-w-[120px] sm:max-w-none">{label}</span>
          </span>
        );
      })}
      
      {!showAll && remainingCount > 0 && (
        <span 
          className="inline-flex items-center px-3 py-2 rounded-full text-xs sm:text-sm font-medium bg-gray-100 text-gray-600 touch-manipulation"
          aria-label={`${remainingCount} more amenities`}
          style={{ minHeight: '32px' }}
        >
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};